import { createSlice } from '@reduxjs/toolkit'
import {
  fetchCallVolume,
  fetchMessageCountAnalytics,
  fetchSalesTrends,
  fetchBookings,
  fetchRatings,
  fetchCommissionReturns,
  fetchTopBrokers,
  fetchBrokers,
  fetchTeamMembers,
  fetchMostViewedPropertiesAgent,
  fetchReviewCounts,
  fetchFilterSearches,
  fetchAdminTotalMessages,
  fetchAdminSavedItems,
  fetchTotalAppDownloads,
} from '../actions/analyticsAction.js'

const initialState = {
  propertyInteractions: [],
  interestedProperties: [],
  interestedUser: [],
  trendingAnalytics: [],
  mostViewedProperties: [],
  savedProperties: [],
  dislikedProperties: [],
  likedProperties: [],
  messageCount: [],
  hourlyPropertyViews: [],
  platformRatingStatistics: [], // New state
  userActivity: null, // New state for user activity analytics
  // Agent Dashboard Analytics
  callVolume: [],
  messageCountAnalytics: [],
  salesTrends: [],
  bookings: [],
  ratings: [],
  commissionReturns: [],
  topBrokers: [],
  brokers: [],
  adminTotalMessages: null,
  adminSavedItems: null,
  teamMembers: [],
  mostViewedPropertiesAgent: [],
  reviewCounts: [],
  filterSearches: [],
  totalAppDownloads: null,
  loading: false,
  error: null,
}

const analyticsSlice = createSlice({
  name: 'analytics',
  initialState,
  reducers: {
    resetAnalyticsState: state => {
      state.propertyInteractions = null
      state.interestedProperties = null
      state.interestedUser = null
      state.trendingAnalytics = null
      state.mostViewedProperties = null
      state.savedProperties = null
      state.likedProperties = null
      state.messageCount = null
      state.hourlyPropertyViews = null
      state.platformRatingStatistics = null // Reset new state
      state.userActivity = null // Reset user activity state
      // Reset agent dashboard analytics
      state.callVolume = null
      state.messageCountAnalytics = null
      state.salesTrends = null
      state.bookings = null
      state.ratings = null
      state.commissionReturns = null
      state.topBrokers = null
      state.teamMembers = null
      state.mostViewedPropertiesAgent = null
      state.reviewCounts = null
      state.filterSearches = null
      state.loading = false
      state.error = null
    },
    setStatusMessage: (state, action) => {
      state.statusMessage = action.payload
    },
    getPropertyInteractionsRequest: state => {
      state.loading = true
      state.error = null
    },
    getPropertyInteractionsSuccess: (state, action) => {
      state.loading = false
      state.propertyInteractions = action.payload
    },
    getPropertyInteractionsFail: (state, action) => {
      state.loading = false
      state.error = action.payload
    },

    getInterestedPropertiesRequest: state => {
      state.loading = true
      state.error = null
    },
    getInterestedPropertiesSuccess: (state, action) => {
      state.loading = false
      state.interestedProperties = action.payload
    },
    getInterestedPropertiesFail: (state, action) => {
      state.loading = false
      state.error = action.payload
    },

    getInterestedUserRequest: state => {
      state.loading = true
      state.error = null
    },
    getInterestedUserSuccess: (state, action) => {
      state.loading = false
      state.interestedUser = action.payload
    },
    getInterestedUserFail: (state, action) => {
      state.loading = false
      state.error = action.payload
    },

    getTrendingAnalyticsRequest: state => {
      state.loading = true
      state.error = null
    },
    getTrendingAnalyticsSuccess: (state, action) => {
      state.loading = false
      state.trendingAnalytics = action.payload
    },
    getTrendingAnalyticsFail: (state, action) => {
      state.loading = false
      state.error = action.payload
    },

    getMostViewedPropertiesRequest: state => {
      state.loading = true
      state.error = null
    },
    getMostViewedPropertiesSuccess: (state, action) => {
      state.loading = false
      state.mostViewedProperties = action.payload
    },
    getMostViewedPropertiesFail: (state, action) => {
      state.loading = false
      state.error = action.payload
    },

    getSavedPropertiesRequest: state => {
      state.loading = true
      state.error = null
    },
    getSavedPropertiesSuccess: (state, action) => {
      state.loading = false
      state.savedProperties = action.payload
    },
    getSavedPropertiesFail: (state, action) => {
      state.loading = false
      state.error = action.payload
    },

    getDislikedPropertiesRequest: state => {
      state.loading = true
      state.error = null
    },
    getDislikedPropertiesSuccess: (state, action) => {
      state.loading = false
      state.dislikedProperties = action.payload
    },
    getDislikedPropertiesFail: (state, action) => {
      state.loading = false
      state.error = action.payload
    },

    getLikedPropertiesRequest: state => {
      state.loading = true
      state.error = null
    },
    getLikedPropertiesSuccess: (state, action) => {
      state.loading = false
      state.likedProperties = action.payload
    },
    getLikedPropertiesFail: (state, action) => {
      state.loading = false
      state.error = action.payload
    },

    getMessageCountRequest: state => {
      state.loading = true
      state.error = null
    },
    getMessageCountSuccess: (state, action) => {
      state.loading = false
      state.messageCount = action.payload
    },
    getMessageCountFail: (state, action) => {
      state.loading = false
      state.error = action.payload
    },

    getAdminTotalMessagesRequest: state => {
      state.loading = true
      state.error = null
    },
    getAdminTotalMessagesSuccess: (state, action) => {
      state.loading = false
      state.adminTotalMessages = action.payload
    },
    getAdminTotalMessagesFail: (state, action) => {
      state.loading = false
      state.error = action.payload
    },

    getAdminSavedItemsRequest: state => {
      state.loading = true
      state.error = null
    },
    getAdminSavedItemsSuccess: (state, action) => {
      state.loading = false
      state.adminSavedItems = action.payload
    },
    getAdminSavedItemsFail: (state, action) => {
      state.loading = false
      state.error = action.payload
    },

    getHourlyPropertyViewsRequest: state => {
      state.loading = true
      state.error = null
    },
    getHourlyPropertyViewsSuccess: (state, action) => {
      state.loading = false
      state.hourlyPropertyViews = action.payload
    },
    getHourlyPropertyViewsFail: (state, action) => {
      state.loading = false
      state.error = action.payload
    },
    getPlatformRatingStatisticsRequest: state => {
      state.loading = true
      state.error = null
    },
    getPlatformRatingStatisticsSuccess: (state, action) => {
      state.loading = false
      state.platformRatingStatistics = action.payload
    },
    getPlatformRatingStatisticsFail: (state, action) => {
      state.loading = false
      state.error = action.payload
    },

    getUserActivityRequest: state => {
      state.loading = true
      state.error = null
    },
    getUserActivitySuccess: (state, action) => {
      state.loading = false
      state.userActivity = action.payload
    },
    getUserActivityFail: (state, action) => {
      state.loading = false
      state.error = action.payload
    },

    // Agent Dashboard Analytics Actions
    getCallVolumeRequest: state => {
      state.loading = true
      state.error = null
    },
    getCallVolumeSuccess: (state, action) => {
      state.loading = false
      state.callVolume = action.payload
    },
    getCallVolumeFail: (state, action) => {
      state.loading = false
      state.error = action.payload
    },

    getMessageCountAnalyticsRequest: state => {
      state.loading = true
      state.error = null
    },
    getMessageCountAnalyticsSuccess: (state, action) => {
      state.loading = false
      state.messageCountAnalytics = action.payload
    },
    getMessageCountAnalyticsFail: (state, action) => {
      state.loading = false
      state.error = action.payload
    },

    getSalesTrendsRequest: state => {
      state.loading = true
      state.error = null
    },
    getSalesTrendsSuccess: (state, action) => {
      state.loading = false
      state.salesTrends = action.payload
    },
    getSalesTrendsFail: (state, action) => {
      state.loading = false
      state.error = action.payload
    },

    getBookingsRequest: state => {
      state.loading = true
      state.error = null
    },
    getBookingsSuccess: (state, action) => {
      state.loading = false
      state.bookings = action.payload
    },
    getBookingsFail: (state, action) => {
      state.loading = false
      state.error = action.payload
    },

    getRatingsRequest: state => {
      state.loading = true
      state.error = null
    },
    getRatingsSuccess: (state, action) => {
      state.loading = false
      state.ratings = action.payload
    },
    getRatingsFail: (state, action) => {
      state.loading = false
      state.error = action.payload
    },

    getCommissionReturnsRequest: state => {
      state.loading = true
      state.error = null
    },
    getCommissionReturnsSuccess: (state, action) => {
      state.loading = false
      state.commissionReturns = action.payload
    },
    getCommissionReturnsFail: (state, action) => {
      state.loading = false
      state.error = action.payload
    },

    getTopBrokersRequest: state => {
      state.loading = true
      state.error = null
    },
    getTopBrokersSuccess: (state, action) => {
      state.loading = false
      state.topBrokers = action.payload
    },
    getTopBrokersFail: (state, action) => {
      state.loading = false
      state.error = action.payload
    },

    getBrokersRequest: state => {
      state.loading = true
      state.error = null
    },
    getBrokersSuccess: (state, action) => {
      state.loading = false
      state.brokers = action.payload
    },
    getBrokersFail: (state, action) => {
      state.loading = false
      state.error = action.payload
    },

    getTeamMembersRequest: state => {
      state.loading = true
      state.error = null
    },
    getTeamMembersSuccess: (state, action) => {
      state.loading = false
      state.teamMembers = action.payload
    },
    getTeamMembersFail: (state, action) => {
      state.loading = false
      state.error = action.payload
    },

    getMostViewedPropertiesAgentRequest: state => {
      state.loading = true
      state.error = null
    },
    getMostViewedPropertiesAgentSuccess: (state, action) => {
      state.loading = false
      state.mostViewedPropertiesAgent = action.payload
    },
    getMostViewedPropertiesAgentFail: (state, action) => {
      state.loading = false
      state.error = action.payload
    },

    getReviewCountsRequest: state => {
      state.loading = true
      state.error = null
    },
    getReviewCountsSuccess: (state, action) => {
      state.loading = false
      state.reviewCounts = action.payload
    },
    getReviewCountsFail: (state, action) => {
      state.loading = false
      state.error = action.payload
    },
  },
  extraReducers: builder => {
    builder
      // Call volume
      .addCase(fetchCallVolume.pending, state => {
        state.loading = true
        state.error = null
      })
      .addCase(fetchCallVolume.fulfilled, (state, action) => {
        state.loading = false
        state.callVolume = action.payload
      })
      .addCase(fetchCallVolume.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload
      })

      // Message count analytics
      .addCase(fetchMessageCountAnalytics.pending, state => {
        state.loading = true
        state.error = null
      })
      .addCase(fetchMessageCountAnalytics.fulfilled, (state, action) => {
        state.loading = false
        state.messageCountAnalytics = action.payload
      })
      .addCase(fetchMessageCountAnalytics.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload
      })

      // Sales trends
      .addCase(fetchSalesTrends.pending, state => {
        state.loading = true
        state.error = null
      })
      .addCase(fetchSalesTrends.fulfilled, (state, action) => {
        state.loading = false
        state.salesTrends = action.payload
      })
      .addCase(fetchSalesTrends.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload
      })

      // Bookings
      .addCase(fetchBookings.pending, state => {
        state.loading = true
        state.error = null
      })
      .addCase(fetchBookings.fulfilled, (state, action) => {
        state.loading = false
        state.bookings = action.payload
      })
      .addCase(fetchBookings.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload
      })

      // Ratings
      .addCase(fetchRatings.pending, state => {
        state.loading = true
        state.error = null
      })
      .addCase(fetchRatings.fulfilled, (state, action) => {
        state.loading = false
        state.ratings = action.payload
      })
      .addCase(fetchRatings.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload
      })

      // Commission returns
      .addCase(fetchCommissionReturns.pending, state => {
        state.loading = true
        state.error = null
      })
      .addCase(fetchCommissionReturns.fulfilled, (state, action) => {
        state.loading = false
        state.commissionReturns = action.payload
      })
      .addCase(fetchCommissionReturns.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload
      })

      // Top brokers
      .addCase(fetchTopBrokers.pending, state => {
        state.loading = true
        state.error = null
      })
      .addCase(fetchTopBrokers.fulfilled, (state, action) => {
        state.loading = false
        state.topBrokers = action.payload
      })
      .addCase(fetchTopBrokers.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload
      })

      // Team members
      .addCase(fetchTeamMembers.pending, state => {
        state.loading = true
        state.error = null
      })
      .addCase(fetchTeamMembers.fulfilled, (state, action) => {
        state.loading = false
        state.teamMembers = action.payload
      })
      .addCase(fetchTeamMembers.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload
      })

      // Most viewed properties (agent)
      .addCase(fetchMostViewedPropertiesAgent.pending, state => {
        state.loading = true
        state.error = null
      })
      .addCase(fetchMostViewedPropertiesAgent.fulfilled, (state, action) => {
        state.loading = false
        state.mostViewedPropertiesAgent = action.payload
      })
      .addCase(fetchMostViewedPropertiesAgent.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload
      })

      // Review counts
      .addCase(fetchReviewCounts.pending, state => {
        state.loading = true
        state.error = null
      })
      .addCase(fetchReviewCounts.fulfilled, (state, action) => {
        state.loading = false
        state.reviewCounts = action.payload
      })
      .addCase(fetchReviewCounts.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload
      })
      // Brokers
      .addCase(fetchBrokers?.pending, state => {
        state.loading = true
        state.error = null
      })
      .addCase(fetchBrokers?.fulfilled, (state, action) => {
        state.loading = false
        state.brokers = action.payload
      })
      .addCase(fetchBrokers?.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload
      })
      // Filter searches
      .addCase(fetchFilterSearches.pending, state => {
        state.loading = true
        state.error = null
      })
      .addCase(fetchFilterSearches.fulfilled, (state, action) => {
        state.loading = false
        state.filterSearches = action.payload
      })
      .addCase(fetchFilterSearches.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload
      })
      // Admin total messages
      .addCase(fetchAdminTotalMessages.pending, state => {
        state.loading = true
        state.error = null
      })
      .addCase(fetchAdminTotalMessages.fulfilled, (state, action) => {
        state.loading = false
        state.adminTotalMessages = action.payload
      })
      .addCase(fetchAdminTotalMessages.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload
      })

      // Admin saved items
      .addCase(fetchAdminSavedItems.pending, state => {
        state.loading = true
        state.error = null
      })
      .addCase(fetchAdminSavedItems.fulfilled, (state, action) => {
        state.loading = false
        state.adminSavedItems = action.payload
      })
      .addCase(fetchAdminSavedItems.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload
      })
      // Total app downloads
      .addCase(fetchTotalAppDownloads.pending, state => {
        state.loading = true
        state.error = null
      })
      .addCase(fetchTotalAppDownloads.fulfilled, (state, action) => {
        state.loading = false
        state.totalAppDownloads = action.payload
      })
      .addCase(fetchTotalAppDownloads.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload
      })
  },
})
export const {
  resetAnalyticsState,
  setStatusMessage,
  getPropertyInteractionsRequest,
  getPropertyInteractionsSuccess,
  getPropertyInteractionsFail,
  getInterestedPropertiesRequest,
  getInterestedPropertiesSuccess,
  getInterestedPropertiesFail,
  getInterestedUserRequest,
  getInterestedUserSuccess,
  getInterestedUserFail,
  getTrendingAnalyticsRequest,
  getTrendingAnalyticsSuccess,
  getTrendingAnalyticsFail,
  getMostViewedPropertiesRequest,
  getMostViewedPropertiesSuccess,
  getMostViewedPropertiesFail,
  getSavedPropertiesRequest,
  getSavedPropertiesSuccess,
  getSavedPropertiesFail,
  getDislikedPropertiesRequest,
  getDislikedPropertiesSuccess,
  getDislikedPropertiesFail,
  getLikedPropertiesRequest,
  getLikedPropertiesSuccess,
  getLikedPropertiesFail,
  getMessageCountRequest,
  getMessageCountSuccess,
  getMessageCountFail,
  getHourlyPropertyViewsRequest,
  getHourlyPropertyViewsSuccess,
  getHourlyPropertyViewsFail,
  getPlatformRatingStatisticsRequest,
  getPlatformRatingStatisticsSuccess,
  getPlatformRatingStatisticsFail,
  getUserActivityRequest,
  getUserActivitySuccess,
  getUserActivityFail,
  // Agent Dashboard Analytics Actions
  getCallVolumeRequest,
  getCallVolumeSuccess,
  getCallVolumeFail,
  getMessageCountAnalyticsRequest,
  getMessageCountAnalyticsSuccess,
  getMessageCountAnalyticsFail,
  getSalesTrendsRequest,
  getSalesTrendsSuccess,
  getSalesTrendsFail,
  getBookingsRequest,
  getBookingsSuccess,
  getBookingsFail,
  getRatingsRequest,
  getRatingsSuccess,
  getRatingsFail,
  getCommissionReturnsRequest,
  getCommissionReturnsSuccess,
  getCommissionReturnsFail,
  getTopBrokersRequest,
  getTopBrokersSuccess,
  getTopBrokersFail,
  getTeamMembersRequest,
  getTeamMembersSuccess,
  getTeamMembersFail,
  getMostViewedPropertiesAgentRequest,
  getMostViewedPropertiesAgentSuccess,
  getMostViewedPropertiesAgentFail,
  getReviewCountsRequest,
  getReviewCountsSuccess,
  getReviewCountsFail,
} = analyticsSlice.actions

export default analyticsSlice.reducer